import { type JSX } from 'react'
import { Eye, Globe, History, Mail, MessageSquare, Pencil, Phone, Users } from 'lucide-react'
import type { ClientRecord } from '../../../hooks/useClients.js'
import type { ClientGroupOption } from '../../../hooks/useClientGroups.js'
import { formatDate } from '../../../utils/formatDate.js'

interface ClientsTableProps {
  clients: ClientRecord[]
  groups: ClientGroupOption[]
  loading: boolean
  onView: (id: string) => void
  onEdit: (id: string) => void
  onHistory: (id: string) => void
}

/** Radius cap shared with the inbox surface. */
const R = 'rounded-[3px]'

const HEADERS = ['Client', 'Phone Number', 'Group / Channel', 'Status', 'Created / Updated'] as const

/** Light-tinted avatar palette — initials on a soft tinted circle, like the reference mockup. */
const AVATAR_TINTS = [
  'bg-indigo-50 border border-indigo-200 text-indigo-700',
  'bg-blue-50 border border-blue-200 text-blue-700',
  'bg-emerald-50 border border-emerald-200 text-emerald-700',
  'bg-amber-50 border border-amber-200 text-amber-700',
  'bg-rose-50 border border-rose-200 text-rose-700',
  'bg-violet-50 border border-violet-200 text-violet-700',
]

export function getInitials(name: string | null, email: string): string {
  if (name) {
    const parts = name.split(' ').filter(Boolean)
    if (parts.length >= 2) return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase()
    if (parts.length === 1) return (parts[0] ?? email).slice(0, 2).toUpperCase()
  }
  return email.slice(0, 2).toUpperCase()
}

export function getAvatarColor(email: string): string {
  let hash = 0
  for (let i = 0; i < email.length; i++) hash = email.charCodeAt(i) + ((hash << 5) - hash)
  return AVATAR_TINTS[Math.abs(hash) % AVATAR_TINTS.length] ?? AVATAR_TINTS[0]!
}

/** Group names double as channels ("Voice Clients", "Email Clients"…) — tint + icon per channel. */
export function groupPill(name: string): { Icon: typeof Users; cls: string } {
  const n = name.toLowerCase()
  if (n.includes('voice') || n.includes('call') || n.includes('phone')) return { Icon: Phone, cls: 'bg-blue-50 text-blue-700 border-blue-200' }
  if (n.includes('email') || n.includes('mail')) return { Icon: Mail, cls: 'bg-amber-50 text-amber-700 border-amber-200' }
  if (n.includes('chat') || n.includes('message')) return { Icon: MessageSquare, cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' }
  if (n.includes('web')) return { Icon: Globe, cls: 'bg-slate-100 text-slate-700 border-slate-200' }
  return { Icon: Users, cls: 'bg-slate-100 text-slate-700 border-slate-200' }
}

export function StatusBadge({ status }: { status: boolean }): JSX.Element {
  return (
    <span className={`inline-flex items-center gap-1.5 border ${R} px-2 py-0.5 text-[11px] font-medium ${status ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-rose-200 bg-rose-50 text-rose-600'}`}>
      <span className={`h-1.5 w-1.5 rounded-[3px] ${status ? 'bg-emerald-500' : 'bg-rose-400'}`} />
      {status ? 'Active' : 'Inactive'}
    </span>
  )
}

const ACTION_HOVER: Record<'view' | 'history' | 'edit' | 'open', string> = {
  view: 'hover:bg-slate-100 hover:text-blue-600',
  open: 'hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200',
  history: 'hover:bg-amber-50 hover:text-amber-700 hover:border-amber-200',
  edit: 'hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200',
}

export function ActionButton({ onClick, Icon, label, hover }: {
  onClick: () => void
  Icon: typeof Eye
  label: string
  hover: keyof typeof ACTION_HOVER
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className={`inline-flex items-center gap-1 border border-slate-200 bg-white px-2.5 py-1 text-[12px] font-medium text-slate-600 shadow-sm transition-colors ${R} ${ACTION_HOVER[hover]}`}
    >
      <Icon className="h-3.5 w-3.5 text-slate-400" />
      {label}
    </button>
  )
}

/** Machine-generated handles (voice_visitor_…) read better in mono. */
export const isSystemName = (name: string | null): boolean => !!name && !name.includes(' ') && name.includes('_')

export default function ClientsTable({ clients, groups, loading, onView, onEdit, onHistory }: ClientsTableProps): JSX.Element {
  const groupName = (id: string) => groups.find((g) => g.id === id)?.name ?? 'No Group'

  return (
    <table className="w-full border-collapse text-left text-xs">
      <thead className="sticky top-0 z-10">
        <tr className="border-b border-slate-200 bg-slate-50 select-none">
          {HEADERS.map((h) => (
            <th key={h} className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{h}</th>
          ))}
          <th className="w-[260px] px-4 py-3 pr-6 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">Quick Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-200">
        {loading && Array.from({ length: 6 }).map((_, i) => (
          <tr key={i}>
            <td className="px-4 py-4" colSpan={6}>
              <div className="h-4 w-full animate-pulse rounded-[3px] bg-slate-100" />
            </td>
          </tr>
        ))}
        {!loading && clients.length === 0 && (
          <tr>
            <td colSpan={6} className="px-4 py-14 text-center text-[13px] text-slate-400">
              No clients found
            </td>
          </tr>
        )}
        {!loading && clients.map((c) => {
          const g = groupName(c.group_id)
          const pill = groupPill(g)
          const mono = isSystemName(c.full_name)
          return (
            <tr
              key={c.id}
              onClick={() => onView(c.id)}
              className="group cursor-pointer transition-colors hover:bg-slate-50/70"
            >
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[3px] text-xs font-bold ${getAvatarColor(c.email)}`}>
                    {getInitials(c.full_name, c.email)}
                  </div>
                  <div className="min-w-0">
                    <span className={`block truncate font-semibold text-slate-900 transition-colors group-hover:text-[#3366FF] ${mono ? 'font-mono' : ''}`}>{c.full_name || '—'}</span>
                    <p className={`truncate text-[11px] text-slate-500 ${mono ? 'font-mono' : ''}`}>{c.email}</p>
                  </div>
                </div>
              </td>
              <td className="whitespace-nowrap px-4 py-3">
                {c.phone_number ? (
                  <span className="inline-flex items-center gap-1.5 font-mono text-slate-600">
                    <Phone className="h-3.5 w-3.5 text-slate-400" />{c.phone_number}
                  </span>
                ) : <span className="text-slate-400">-</span>}
              </td>
              <td className="whitespace-nowrap px-4 py-3">
                <span className={`inline-flex items-center gap-1.5 border ${R} px-2 py-0.5 text-[11px] font-medium ${pill.cls}`}>
                  <pill.Icon className="h-3 w-3" />{g}
                </span>
              </td>
              <td className="whitespace-nowrap px-4 py-3">
                <div className="flex items-center">
                  <StatusBadge status={c.status} />
                  {c.locked && (
                    <span className={`ml-1.5 inline-flex items-center border border-amber-200 ${R} bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700`}>Locked</span>
                  )}
                </div>
              </td>
              <td className="whitespace-nowrap px-4 py-3 text-slate-500">{formatDate(c.createdAt)}</td>
              <td className="whitespace-nowrap px-4 py-3 pr-6 text-right" onClick={(e) => e.stopPropagation()}>
                <div className="inline-flex items-center justify-end gap-1.5">
                  <ActionButton onClick={() => onView(c.id)} Icon={Eye} label="View" hover="view" />
                  <ActionButton onClick={() => onHistory(c.id)} Icon={History} label="History" hover="history" />
                  <ActionButton onClick={() => onEdit(c.id)} Icon={Pencil} label="Edit" hover="edit" />
                </div>
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
